import { forwardRef } from 'react';

import './Link.css';

import { useNavigate, Link as RouterLink } from 'react-router-dom';

import { Icon, IconItem } from '../icon/Icon';

type LinkProps = {
  href: string;
  ariaCurrent?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true' | 'false';
  text?: string | React.ReactNode;
  ariaLabel?: string;
  className?: string;
  color?: 'pink' | 'blue' | 'dark' | 'white' | 'gray';
  colorOnHover?: 'pink' | 'blue' | 'dark' | 'white' | 'gray';
  id?: string;
  icon?: IconItem;
  iconPosition?: 'left' | 'right';
  isDisabled?: boolean;
  isWrapText?: boolean;
  target?: '_blank' | '_self' | '_parent' | '_top';
  rel?: string;
  children?: React.ReactNode;
  tabIndex?: number;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLAnchorElement>) => void;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    ariaCurrent,
    href,
    text,
    ariaLabel,
    className = '',
    color = 'white',
    colorOnHover = 'white',
    id,
    icon,
    iconPosition = 'left',
    isDisabled = false,
    isWrapText = false,
    target = '_self',
    rel,
    children,
    tabIndex,
    onClick,
    onKeyDown,
  },
  ref
) {
  const linkIcon = <Icon className={`link-icon-${iconPosition}`} icon={icon} />;

  const classes = [
    'link',
    `link-${color}`,
    isDisabled ? 'link-disabled' : '',
    isWrapText ? 'wrap-text' : '',
    className,
    `link-onhover-${colorOnHover}`,
  ].join(' ');

  const isInternalLink = href?.startsWith('/');

  const navigate = useNavigate();

  return (
    <RouterLink
      aria-current={ariaCurrent}
      ref={ref}
      to={isDisabled ? undefined : href}
      className={classes}
      id={id}
      aria-label={ariaLabel}
      target={target}
      rel={rel || (target === '_blank' ? 'noopener noreferrer' : undefined)}
      tabIndex={isDisabled ? -1 : tabIndex}
      aria-disabled={isDisabled ? 'true' : undefined}
      onClick={
        isInternalLink
          ? (e) => {
              e.preventDefault();
              if (onClick) onClick(e);
              navigate(href);
            }
          : undefined
      }
      onKeyDown={(e) => {
        if (isDisabled) return;
        if (typeof onKeyDown === 'function') onKeyDown(e);
      }}
    >
      {icon && iconPosition === 'left' && linkIcon}
      {text}
      {children}
      {icon && iconPosition === 'right' && linkIcon}
    </RouterLink>
  );
});
